import {
  FunctionComponent,
  ReactNode,
  Children,
  useState,
  useEffect,
  useRef,
} from "react";

interface IAutoplayCarousel {
  children: ReactNode;
  config: any;
}

type Slot = {
  id: number;
  col: number;
  pos: number;
  instant: boolean;
};

const AutoplayCarousel: FunctionComponent<IAutoplayCarousel> = ({
  config,
  children,
}) => {
  const slides = Children.toArray(children);
  const count = slides.length;

  const delay: number = config.autoplay?.delay ?? 3000;

  const speed: number = config.speed ?? 500;
  const slidesPerView: number = config.slidesPerView ?? 1;
  const rows: number = config.grid?.rows ?? 1;
  const extraClass: string = config.className ?? "";
  const reverse: boolean = config.transition === "left";

  const isGrid = rows > 1 || slidesPerView > 1;

  const totalCols = isGrid ? Math.ceil(count / rows) : count;

  const VISIBLE = isGrid ? Math.floor(slidesPerView) : 1;

  const SLOTS = VISIBLE + 2;

  const slotWidth = isGrid ? 100 / slidesPerView : 100;

  const columns: ReactNode[][] = isGrid
    ? Array.from({ length: totalCols }, (_, c) =>
        Array.from({ length: rows }, (_, r) => slides[c * rows + r]).filter(
          (s) => s != null,
        ),
      )
    : slides.map((s) => [s]);

  const canScroll = totalCols > VISIBLE;

  const [slots, setSlots] = useState<Slot[]>(() =>
    Array.from({ length: SLOTS }, (_, i) => ({
      id: i,
      col: i % totalCols,
      pos: i,
      instant: false,
    })),
  );

  const baseRef = useRef(0);
  const busyRef = useRef(false);
  const [activatedCols, setActivatedCols] = useState<Set<number>>(
    () => new Set(slots.map((s) => s.col)),
  );

  useEffect(() => {
    if (!canScroll) return;

    const advance = () => {
      if (busyRef.current) return;
      busyRef.current = true;

      setSlots((prev) =>
        prev.map((s) => ({ ...s, pos: s.pos - 1, instant: false })),
      );
      baseRef.current = (baseRef.current + 1) % totalCols;

      const nextCol = (baseRef.current + SLOTS - 1) % totalCols;
      setTimeout(() => {
        setActivatedCols((prev) =>
          prev.has(nextCol) ? prev : new Set(prev).add(nextCol),
        );
        setSlots((prev) => {
          const recycled = prev.find((s) => s.pos < 0);
          if (!recycled) return prev;
          return prev.map((s) =>
            s.id === recycled.id
              ? { ...s, pos: SLOTS - 1, col: nextCol, instant: true }
              : s,
          );
        });

        setTimeout(() => {
          setSlots((prev) => prev.map((s) => ({ ...s, instant: false })));
          busyRef.current = false;
        }, 50);
      }, speed);
    };

    const id = setInterval(advance, delay);
    return () => clearInterval(id);
  }, [canScroll, delay, speed, totalCols, VISIBLE, SLOTS]);

  if (!canScroll) {
    return isGrid ? (
      <div className={`w-full relative overflow-hidden ${extraClass}`}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${totalCols}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
            gridAutoFlow: "column",
            height: "100%",
          }}
        >
          {slides}
        </div>
      </div>
    ) : (
      <div className={`w-full overflow-hidden relative ${extraClass}`}>
        <div style={{ width: "100%", height: "100%" }}>{slides[0]}</div>
      </div>
    );
  }

  if (!reverse) {
    return (
      <div className={`w-full relative overflow-hidden ${extraClass}`}>
        {Array.from(activatedCols).map((col) => {
          const holder = slots.find((s) => s.col === col);
          const pos = holder ? holder.pos : SLOTS;
          const instant = holder ? holder.instant : true;

          return (
            <div
              key={col}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: `${slotWidth}%`,
                height: "100%",
                transform: `translateX(${pos * 100}%)`,
                transition: instant ? "none" : `transform ${speed}ms ease`,
                ...(isGrid && {
                  display: "grid",
                  gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
                }),
              }}
            >
              {(columns[col] ?? []).map((slide, i) => (
                <div key={i} style={{ width: "100%", height: "100%" }}>
                  {slide}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`w-full relative overflow-hidden ${extraClass}`}>
      {Array.from(activatedCols).map((col) => {
        const holder = slots.find((s) => s.col === col);
        const pos = holder ? holder.pos : SLOTS;
        const instant = holder ? holder.instant : true;

        return (
          <div
            key={col}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: `${slotWidth}%`,
              height: "100%",
              transform: `translateX(${pos * -100}%)`,
              transition: instant ? "none" : `transform ${speed}ms ease`,
              ...(isGrid && {
                display: "grid",
                gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
              }),
            }}
          >
            {(columns[col] ?? []).map((slide, i) => (
              <div key={i} style={{ width: "100%", height: "100%" }}>
                {slide}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default AutoplayCarousel;
